/**
 * Textile Costing API Service
 *
 * Provides API calls for the consolidated textile costing management view.
 */

import api from './api';

// Note: Django URL is configured as 'costing/' in EEE/urls.py
const BASE_URL = '/costing/api';

export const textileCostingService = {
  /**
   * Get consolidated costing data for an opportunity.
   * Returns all products with their complete cost breakdowns (Greige, Finished, Made-Up).
   *
   * @param {number} opportunityId - The opportunity ID
   * @returns {Promise} API response with consolidated costing data
   */
  getConsolidatedCosting: (opportunityId) =>
    api.get(`${BASE_URL}/consolidated/${opportunityId}/`),

  /**
   * Get detailed costing for a single product (made-up).
   *
   * @param {number} madeupId - The made-up costing ID
   * @returns {Promise} API response with product costing detail
   */
  getProductCosting: (madeupId) =>
    api.get(`${BASE_URL}/product/${madeupId}/`),

  /**
   * Recalculate costs for a costing record.
   *
   * @param {string} costingType - 'greige', 'finished', or 'madeup'
   * @param {number} costingId - The costing record ID
   * @returns {Promise} API response with recalculation result
   */
  recalculateCosting: (costingType, costingId) =>
    api.post(`${BASE_URL}/recalculate/${costingType}/${costingId}/`),

  /**
   * Update costing parameters and trigger recalculation.
   *
   * @param {string} costingType - 'greige', 'finished', or 'madeup'
   * @param {number} costingId - The costing record ID
   * @param {object} data - Updated parameters
   * @returns {Promise} API response with updated costing
   */
  updateCosting: (costingType, costingId, data) =>
    api.patch(`${BASE_URL}/update/${costingType}/${costingId}/`, data),

  /**
   * Perform approval workflow action.
   *
   * @param {string} costingType - 'greige', 'finished', or 'madeup'
   * @param {number} costingId - The costing record ID
   * @param {string} action - 'submit', 'approve', or 'reject'
   * @param {string} comments - Optional comments
   * @returns {Promise} API response with new status
   */
  approvalAction: (costingType, costingId, action, comments = '') =>
    api.post(`${BASE_URL}/approve/${costingType}/${costingId}/`, {
      action,
      comments,
    }),

  /**
   * Get list of opportunities with costing summary.
   *
   * @returns {Promise} API response with opportunities list
   */
  getOpportunitiesWithCosting: () =>
    api.get(`${BASE_URL}/opportunities/`),

  /**
   * Get dashboard counts for all costing types.
   *
   * @returns {Promise} API response with counts
   */
  getDashboardCounts: () =>
    api.get(`${BASE_URL}/dashboard-counts/`),

  /**
   * Get list of cut plans.
   *
   * @returns {Promise} API response with cut plans list
   */
  getCutPlans: () =>
    api.get(`${BASE_URL}/cutplans/`),

  /**
   * Create a new cut plan with default values.
   *
   * @returns {Promise} API response with new cut plan id and name
   */
  createCutPlan: () =>
    api.post(`${BASE_URL}/cutplans/create/`),

  /**
   * Copy a cut plan.
   *
   * @param {number} cutPlanId - The cut plan ID to copy
   * @returns {Promise} API response with new cut plan info
   */
  copyCutPlan: (cutPlanId) =>
    api.post(`${BASE_URL}/cutplans/${cutPlanId}/copy/`),

  /**
   * Get all cut plans for an opportunity (worksheet view).
   *
   * @param {number} opportunityId - The opportunity ID
   * @returns {Promise} API response with opportunity meta + cutplans array
   */
  /**
   * Lightweight opportunity search for the worksheet autocomplete.
   * Uses the costing API so only tc.view_costing is required (not crm.view_opportunity).
   *
   * @param {string} query - Search string (matched against opportunity_number and title)
   * @returns {Promise} API response with [{id, opportunity_number, title}]
   */
  searchOpportunities: (query) =>
    api.get(`${BASE_URL}/opportunity-search/`, { params: { search: query } }),

  getCutPlansByOpportunity: (opportunityId) =>
    api.get(`${BASE_URL}/cutplans/by-opportunity/${opportunityId}/`),

  getGreigeCostingsByOpportunity: (opportunityId) =>
    api.get(`${BASE_URL}/greige/by-opportunity/${opportunityId}/`),

  getFinishedCostingsByOpportunity: (opportunityId) =>
    api.get(`${BASE_URL}/finished/by-opportunity/${opportunityId}/`),

  getMadeUpCostingsByOpportunity: (opportunityId) =>
    api.get(`${BASE_URL}/madeup/by-opportunity/${opportunityId}/`),

  /**
   * Bulk create / update cut plans for an opportunity.
   *
   * @param {number} opportunityId - The opportunity ID
   * @param {Array}  cutplans      - Array of cut plan row objects
   * @returns {Promise} API response with fresh cutplans array
   */
  bulkSaveCutPlans: (opportunityId, cutplans) =>
    api.post(`${BASE_URL}/cutplans/bulk-save/`, { opportunity_id: opportunityId, cutplans }),

  /**
   * Generate Cut Plan PDF (preview or download).
   *
   * @param {number} cutPlanId - The cut plan ID
   * @param {string} mode - 'preview' or 'download'
   * @returns {Promise} API response with PDF blob
   */
  getCutPlanPdf: (cutPlanId, mode = 'preview') =>
    api.get(`${BASE_URL}/cutplans/${cutPlanId}/pdf/?mode=${mode}`, {
      responseType: 'blob',
    }),

  /**
   * Create a new greige fabric costing with default values.
   *
   * @returns {Promise} API response with new costing id and name
   */
  createGreigeCosting: () =>
    api.post(`${BASE_URL}/greige/create/`),

  /**
   * Create a new finished fabric costing with default values.
   *
   * @returns {Promise} API response with new costing id and name
   */
  createFinishedCosting: () =>
    api.post(`${BASE_URL}/finished/create/`),

  /**
   * Create a new made-up costing with default values.
   *
   * @returns {Promise} API response with new costing id and name
   */
  createMadeUpCosting: () =>
    api.post(`${BASE_URL}/madeup/create/`),

  /**
   * Get list of greige fabric costings.
   *
   * @returns {Promise} API response with greige costings list
   */
  getGreigeCostings: () =>
    api.get(`${BASE_URL}/greige/`),

  /**
   * Copy a greige fabric costing.
   *
   * @param {number} costingId - The costing ID to copy
   * @returns {Promise} API response with new costing info
   */
  copyGreigeCosting: (costingId) =>
    api.post(`${BASE_URL}/greige/${costingId}/copy/`),

  deleteGreigeCosting: (costingId) =>
    api.delete(`${BASE_URL}/greige/${costingId}/delete/`),

  /**
   * Get list of finished fabric costings.
   *
   * @returns {Promise} API response with finished costings list
   */
  getFinishedCostings: () =>
    api.get(`${BASE_URL}/finished/`),

  /**
   * Copy a finished fabric costing.
   *
   * @param {number} costingId - The costing ID to copy
   * @returns {Promise} API response with new costing info
   */
  copyFinishedCosting: (costingId) =>
    api.post(`${BASE_URL}/finished/${costingId}/copy/`),

  deleteFinishedCosting: (costingId) =>
    api.delete(`${BASE_URL}/finished/${costingId}/delete/`),

  /**
   * Get list of made-up costings.
   *
   * @returns {Promise} API response with made-up costings list
   */
  getMadeUpCostings: () =>
    api.get(`${BASE_URL}/madeup/`),

  /**
   * Copy a made-up costing.
   *
   * @param {number} costingId - The costing ID to copy
   * @returns {Promise} API response with new costing info
   */
  copyMadeUpCosting: (costingId) =>
    api.post(`${BASE_URL}/madeup/${costingId}/copy/`),

  deleteMadeUpCosting: (costingId) =>
    api.delete(`${BASE_URL}/madeup/${costingId}/delete/`),

  deleteCutPlan: (cutPlanId) =>
    api.delete(`${BASE_URL}/cutplans/${cutPlanId}/delete/`),

  // Terry Fabric Costing
  getTerryFabricCostings: () =>
    api.get(`${BASE_URL}/terry-fabric/`),

  getTerryFabricCosting: (costingId) =>
    api.get(`${BASE_URL}/terry-fabric/${costingId}/`),

  createTerryFabricCosting: () =>
    api.post(`${BASE_URL}/terry-fabric/create/`),

  copyTerryFabricCosting: (costingId) =>
    api.post(`${BASE_URL}/terry-fabric/${costingId}/copy/`),

  getTerryFabricCostingsByOpportunity: (opportunityId) =>
    api.get(`${BASE_URL}/terry-fabric/by-opportunity/${opportunityId}/`),

  // Terry Made-Up Costing
  getTerryMadeUpCostings: () =>
    api.get(`${BASE_URL}/terry-madeup/`),

  getTerryMadeUpCosting: (costingId) =>
    api.get(`${BASE_URL}/terry-madeup/${costingId}/`),

  createTerryMadeUpCosting: () =>
    api.post(`${BASE_URL}/terry-madeup/create/`),

  copyTerryMadeUpCosting: (costingId) =>
    api.post(`${BASE_URL}/terry-madeup/${costingId}/copy/`),

  getTerryMadeUpCostingsByOpportunity: (opportunityId) =>
    api.get(`${BASE_URL}/terry-madeup/by-opportunity/${opportunityId}/`),

  /**
   * Submit a costing for approval.
   *
   * @param {string} costingType - 'greige', 'finished', or 'madeup'
   * @param {number} costingId - The costing ID
   * @returns {Promise} API response with new status
   */
  submitForApproval: (costingType, costingId) =>
    api.post(`${BASE_URL}/approve/${costingType}/${costingId}/`, { action: 'submit' }),

  // Opportunity-level approval workflow
  submitOpportunityForApproval: (opportunityId) =>
    api.post(`${BASE_URL}/opportunity/${opportunityId}/submit-for-approval/`, { action: 'submit' }),

  cancelOpportunityApproval: (opportunityId, reason = '') =>
    api.post(`${BASE_URL}/opportunity/${opportunityId}/cancel-approval/`, { action: 'cancel', reason }),

  getOpportunityApprovalStatus: (opportunityId) =>
    api.get(`${BASE_URL}/opportunity/${opportunityId}/approval-status/`),

  /**
   * Repeat-order: preview what a copy of this opportunity would include
   * (editable header defaults, line items, and count of sheets to be copied).
   *
   * @param {number} opportunityId - Source opportunity ID
   * @returns {Promise}
   */
  getOpportunityCopyPreview: (opportunityId) =>
    api.get(`${BASE_URL}/opportunity/${opportunityId}/copy/`),

  /**
   * Repeat-order: clone the opportunity + its approved costing sheets.
   *
   * @param {number} opportunityId - Source opportunity ID
   * @param {object} payload - { title, expected_closing_date, expected_revenue,
   *   line_items: [{ source_line_item_id, quantity, target_price }] }
   * @returns {Promise} API response with new_opportunity_id/number + copied counts
   */
  copyOpportunityWithCostings: (opportunityId, payload) =>
    api.post(`${BASE_URL}/opportunity/${opportunityId}/copy/`, payload),

  // Set Costing
  getSetCostings: () =>
    api.get(`${BASE_URL}/set-costings/`),

  getSetCosting: (costingId) =>
    api.get(`${BASE_URL}/set-costings/${costingId}/`),

  getSetCostingsByOpportunity: (opportunityId) =>
    api.get(`${BASE_URL}/set-costings/by-opportunity/${opportunityId}/`),

  getWorkspaceOpportunities: () =>
    api.get(`${BASE_URL}/workspace/`),

  // Component Master (accessories, packaging, manufacturing operations, ...)
  getComponentTypes: (params) =>
    api.get(`${BASE_URL}/component-types/`, { params }),

  getComponentTypeFormOptions: () =>
    api.get(`${BASE_URL}/component-types/form-options/`),

  createComponentType: (data) =>
    api.post(`${BASE_URL}/component-types/`, data),

  updateComponentType: (id, data) =>
    api.patch(`${BASE_URL}/component-types/${id}/`, data),

  deleteComponentType: (id) =>
    api.delete(`${BASE_URL}/component-types/${id}/`),

  // Price Revision - re-price approved costings as new versions, approve once
  getPriceRevisions: (params) =>
    api.get(`${BASE_URL}/price-revisions/`, { params }),

  getPriceRevision: (id) =>
    api.get(`${BASE_URL}/price-revisions/${id}/`),

  getPriceRevisionFormOptions: () =>
    api.get(`${BASE_URL}/price-revisions/form-options/`),

  createPriceRevision: (data) =>
    api.post(`${BASE_URL}/price-revisions/`, data),

  updatePriceRevision: (id, data) =>
    api.patch(`${BASE_URL}/price-revisions/${id}/`, data),

  deletePriceRevision: (id) =>
    api.delete(`${BASE_URL}/price-revisions/${id}/`),

  savePriceRevisionRates: (id, data) =>
    api.post(`${BASE_URL}/price-revisions/${id}/rates/`, data),

  refreshPriceRevisionRates: (id) =>
    api.post(`${BASE_URL}/price-revisions/${id}/refresh-rates/`),

  setPriceRevisionItemIncluded: (id, itemId, isIncluded) =>
    api.post(`${BASE_URL}/price-revisions/${id}/items/${itemId}/include/`, { is_included: isIncluded }),

  // action: calculate | submit | approve | reject | cancel | create-quotations
  priceRevisionAction: (id, action, data = {}) =>
    api.post(`${BASE_URL}/price-revisions/${id}/${action}/`, data),
};

export default textileCostingService;
